import { Text, Title } from "@clipboard-health/ui-react";
import { isDefined } from "@clipboard-health/util-ts";
import { Box, Button, Stack } from "@mui/material";
import { WorkedBreakAuthorization } from "@src/appV2/Shifts/Shift/WorkedBreakAuthorization/WorkedBreakAuthorizationPage";
import { TimeRange } from "@src/lib/interface/src";

export type WorkedBreakConfirmationProps = {
  isTimeEditOpen: boolean;
  lunchInOutTimeRange: TimeRange;
  workedBreakAuthorization: WorkedBreakAuthorization;
  onEditTimesClick: () => void;
  onConfirmClick: () => void;
};

export function WorkedBreakConfirmation(props: WorkedBreakConfirmationProps): JSX.Element {
  const {
    isTimeEditOpen,
    lunchInOutTimeRange,
    workedBreakAuthorization,
    onEditTimesClick,
    onConfirmClick,
  } = props;

  const authorizerName = workedBreakAuthorization.authorizingEmployee?.name;
  const authorizerJobTitle = workedBreakAuthorization.authorizingEmployee?.jobTitle;

  const breakType =
    !isDefined(lunchInOutTimeRange.start) || !isDefined(lunchInOutTimeRange.end)
      ? "Skipped"
      : "Incomplete";

  const doesAuthorizerExists = isDefined(authorizerName) && authorizerName !== "";

  return (
    <Box display="flex" flexDirection="column" sx={{ height: "100%", overflowY: "hidden" }}>
      <Stack flexGrow={1} sx={{ height: "100%", overflowY: "auto" }} spacing={1}>
        <Title component="h1" marginBottom={2} bold={true}>
          Is this correct?
        </Title>
        <Text>
          This timesheet contains&nbsp;
          {`${breakType === "Skipped" ? "a" : "an"} ${breakType.toLowerCase()}`} break. By signing
          this timesheet,&nbsp;
          <strong>
            you confirm the following {doesAuthorizerExists ? "statements" : "statement"} from the
            Clipboard professional&nbsp;
            {doesAuthorizerExists ? "are" : "is"} accurate:
          </strong>
        </Text>

        <ul>
          {doesAuthorizerExists ? (
            <li>
              <Text>
                {`${breakType} break was authorized by ${authorizerName.trim()}${
                  authorizerJobTitle ? `, ${authorizerJobTitle.trim()}` : ""
                }.`}
              </Text>
            </li>
          ) : null}
          <li>
            <Text>Clipboard professional: "{workedBreakAuthorization.reason.trim()}"</Text>
          </li>
        </ul>
        {doesAuthorizerExists ? null : (
          <Text>
            Note the Clipboard professional did not provide a name of a facility employee who
            authorized the {breakType.toLowerCase()} break, despite given the opportunity to do so.
          </Text>
        )}
        <Text>
          If this timesheet is signed, the facility admin will be notified of the&nbsp;
          {`${breakType.toLowerCase()}`} break and will have three business days to accept or
          dispute the additional charge.
        </Text>
      </Stack>
      <Stack direction="row" justifyContent="space-between" spacing={2} sx={{ paddingTop: 1 }}>
        <Button
          variant="contained"
          onClick={onEditTimesClick}
          sx={{ flexGrow: 1, flexShrink: 1, flexBasis: "50%" }}
        >
          Edit break times
        </Button>
        <Button
          variant="outlined"
          onClick={onConfirmClick}
          disabled={isTimeEditOpen}
          sx={{ flexGrow: 1, flexShrink: 1, flexBasis: "50%" }}
        >
          Confirm
        </Button>
      </Stack>
    </Box>
  );
}
